'use strict';

var React = require('react');
var reactUtils = require('@sravni/react-utils');
var getCurrentStep = require('../../../helpers/getCurrentStep.js');
var NumberInput_module = require('../styles/NumberInput.module.less.js');
var NumberInputView = require('./NumberInputView.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

const rangeValue = (value, max, min) => {
    if (max === undefined && min === undefined) {
        return Number(value);
    }
    if (max === undefined && min !== undefined) {
        return Math.max(value, min);
    }
    if (max !== undefined && min === undefined) {
        return Math.min(value, max);
    }
    return Math.min(max, Math.max(value, min));
};
const roundToNearestStep = (value, step) => {
    const currentStep = getCurrentStep.getCurrentStep(step, value);
    if (!currentStep) {
        return value;
    }
    if (currentStep < 1) {
        const decimalPart = value - Math.floor(value);
        const integerStep = 1 / currentStep;
        const roundedDecimalPart = Math.round(decimalPart * integerStep) / integerStep;
        return Math.floor(value) + roundedDecimalPart;
    }
    return Math.round(value / currentStep) * currentStep;
};
/**
 * @description Enter a number within certain range with the mouse or keyboard
 *
 * @component
 * @example
 * ```jsx
 * <NumberInput
 *    label="Название поля"
 *    min={30000}
 *    max={1000000}
 *    postfix="₽/мес"
 *    thousandSeparator=" "
 *    decimalScale={2}
 *  />
 * ```
 */
const NumberInput = React__namespace.forwardRef((props, ref) => {
    const { value, min, max, step, decimalScale, fixedDecimalScale, decimalSeparator, thousandSeparator, invalid, disabled, onChange, onBlur, onKeyPress, format, defaultValue, className, ...restProps } = props;
    const formControlContext = reactUtils.useFormControlContext();
    const cx = reactUtils.useStyles(NumberInput_module.default);
    const classNames = cx(className, 'container');
    const condition = decimalSeparator === thousandSeparator;
    // Ensure the decimalSeparator is not equal the thousandSeparator
    if (condition) {
        reactUtils.error({
            condition,
            message: 'Ensure the decimalSeparator is not equal the thousandSeparator',
        })();
    }
    const empty = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const prepareValue = (value) => {
        if (value === undefined || value === empty) {
            return undefined;
        }
        return step ? roundToNearestStep(rangeValue(value, max, min), step) : rangeValue(value, max, min);
    };
    const [innerValue, setInnerValue] = React__namespace.useState();
    const isFirstRender = React__namespace.useRef(true);
    const handleValueChange = (values, sourceInfo) => {
        if (!sourceInfo.event || values.floatValue === innerValue) {
            return;
        }
        setInnerValue(values.floatValue);
        onChange?.(values.floatValue);
    };
    const setPreparedValue = () => {
        setInnerValue((value) => {
            const preparedValue = prepareValue(value);
            if (value !== preparedValue && value !== empty) {
                onChange?.(preparedValue);
            }
            return preparedValue;
        });
    };
    const handleBlur = (event) => {
        setPreparedValue();
        onBlur?.(event);
    };
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            setPreparedValue();
        }
        onKeyPress?.(event);
    };
    React__namespace.useEffect(() => {
        if (!isFirstRender.current) {
            reactUtils.isNumber(value) && setInnerValue(value);
            if (reactUtils.isUndefined(value) || value === '') {
                setInnerValue('');
            }
        }
        else {
            setInnerValue(prepareValue(value ?? defaultValue));
            isFirstRender.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return (React__namespace.createElement("div", { "data-qa": "NumberInput", className: classNames },
        React__namespace.createElement(NumberInputView.NumberInputView, { ...restProps, ref: ref, value: innerValue, thousandSeparator: thousandSeparator, decimalSeparator: decimalSeparator, decimalScale: decimalScale, fixedDecimalScale: fixedDecimalScale, format: format, inputMode: "decimal", invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, onValueChange: handleValueChange, onKeyPress: handleKeyPress, onBlur: handleBlur })));
});
NumberInput.defaultProps = {
    decimalSeparator: ',',
    fixedDecimalScale: true,
    allowedDecimalSeparators: [',', '.'],
    allowNegative: false,
};
NumberInput.displayName = 'NumberInput';

exports.NumberInput = NumberInput;
//# sourceMappingURL=NumberInput.js.map
